import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@repletoryreach/sdk-auth';
import { settings } from '@repletoryreach/sdk-api';
import { GuideDrawer } from './GuideDrawer';
import { GuideTour } from './GuideTour';
import { GUIDES } from './registry';
import type { Guide, TourStep } from './types';
import './guides.css';

interface GuideContextValue {
  /** The guide's main tour, or one of its how-to tours by id. */
  startTour: (id: string, howTo?: string) => void;
  openGuide: (id: string) => void;
  /** A tour is on the screen: anything else that would introduce itself waits for it. */
  tourOpen: boolean;
  /** Whether this person has had a one-time introduction: a tour, or another introduction by its own id. */
  seen: (id: string) => boolean;
  /** Records a one-time introduction as had, on the person's user scope, so it shows once per person. */
  markSeen: (id: string) => void;
  /** False until the saved list has loaded (or failed), so nothing introduces itself twice. */
  seenReady: boolean;
}

// Outside the provider nothing introduces itself: everything counts as seen.
const GuideContext = createContext<GuideContextValue>({
  startTour: () => {}, openGuide: () => {}, tourOpen: false, seen: () => true, markSeen: () => {}, seenReady: false
});

/** The steps of a guide's main tour, or of one of its how-tos. */
export function stepsOf(g: Guide, howTo?: string): TourStep[] {
  return howTo ? g.howTos?.find((h) => h.id === howTo)?.steps ?? g.tour : g.tour;
}

export const useGuides = () => useContext(GuideContext);

/**
 * Which tours this person has seen. Kept on their own user scope in settings (8 October 2026), so a tour opens by
 * itself once per person, not once per browser or phone; this browser keeps a copy too, which covers the moment
 * before settings answer and a settings service that cannot be reached.
 */
const SEEN_SETTING = 'guide.tours.seen';
/** The assistant's one-time greeting (15 October 2026), recorded with the tours. */
export const ASSISTANT_INTRO = 'assistant-intro';
type Seen = Record<string, number>;
function seenKey(userId: string | undefined) { return `tenant.guides.seen.${userId ?? 'anon'}`; }
// Kept in the browser twice (15 October 2026: a skipped tour opened again on a refresh): the browser's saved data,
// and this tab's session, which survives a refresh even where a browser refuses to keep saved data.
const browserStores = (): Storage[] => {
  const out: Storage[] = [];
  try { out.push(window.localStorage); } catch { /* blocked */ }
  try { out.push(window.sessionStorage); } catch { /* blocked */ }
  return out;
};
function readSeen(userId: string | undefined): Seen {
  const seen: Seen = {};
  for (const store of browserStores()) {
    try { Object.assign(seen, JSON.parse(store.getItem(seenKey(userId)) ?? '{}') as Seen); } catch { /* unreadable: the other copies decide */ }
  }
  return seen;
}
function writeSeen(userId: string | undefined, seen: Seen) {
  for (const store of browserStores()) {
    try { store.setItem(seenKey(userId), JSON.stringify(seen)); } catch { /* full or blocked: the other copies keep it */ }
  }
}

/** The guide whose route matches the page most closely: /inbox/compose is Audience's even though /inbox is the Inbox's. */
export function guideFor(pathname: string): Guide | undefined {
  let best: { g: Guide; len: number } | undefined;
  for (const g of Object.values(GUIDES)) {
    for (const r of g.routes) {
      const hit = pathname === r || (r !== '/' && pathname.startsWith(r + '/'));
      if (hit && (!best || r.length > best.len)) best = { g, len: r.length };
    }
  }
  return best?.g;
}

/**
 * Runs the tours and the written guides for the whole console. A module's tour starts by itself only the first time
 * a person opens one of its pages, and from its help button at any time. A guide with autoStart false never starts
 * by itself.
 */
export function GuideProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [tour, setTour] = useState<{ guide: Guide; steps: TourStep[]; key: number; howTo?: string } | null>(null);
  const qc = useQueryClient();
  const storedKey = ['settings', SEEN_SETTING, user?.id];
  const [drawer, setDrawer] = useState<Guide | null>(null);
  const autoStarted = useRef(false);

  // The tours seen, as saved on this person's own user scope.
  const stored = useQuery({
    queryKey: storedKey,
    enabled: !!user?.id,
    queryFn: () => settings.listSettings({ scope: 'user', prefix: SEEN_SETTING })
      .then((r) => {
        // The person's own row, never the empty declaration the key is created with.
        const row = (r.data ?? []).find((x) => x.key === SEEN_SETTING && x.scope === 'user');
        return (row?.value && typeof row.value === 'object' ? row.value : {}) as Seen;
      }),
    staleTime: Infinity,
    retry: 2
  });
  const { mutate: saveSeen } = useMutation({
    mutationFn: (next: Seen) => settings.setSetting<Seen>(SEEN_SETTING, next, { scope: 'user', value_type: 'json' }),
    // The console's own copy follows at once, so nothing reads the record from before this save.
    onSuccess: (_res, next) => qc.setQueryData(storedKey, next)
  });
  const seenNow = useCallback((): Seen => ({ ...(stored.data ?? {}), ...readSeen(user?.id) }), [stored.data, user?.id]);

  // Recorded when a tour starts, and again when it is skipped or finished (owner, 15 October 2026), in the browser and
  // on the person's account. The account copy is brought up to date whenever it lacks something this browser knows,
  // but only once it has been read: writing over a record that could not be read would lose what other devices saw.
  const markSeenId = useCallback((id: string, version = 1) => {
    const server = stored.data;
    const merged: Seen = { ...(server ?? {}), ...readSeen(user?.id) };
    if (!merged[id]) merged[id] = version;
    writeSeen(user?.id, merged);
    if (server && Object.keys(merged).some((k) => !(k in server))) saveSeen(merged);
  }, [user?.id, stored.data, saveSeen]);
  const markSeen = useCallback((g: Guide) => markSeenId(g.id, g.version), [markSeenId]);

  // Once the account's record is read, it learns anything this browser recorded before then.
  useEffect(() => {
    if (!user?.id || !stored.isSuccess) return;
    const server = stored.data;
    const local = readSeen(user.id);
    if (Object.keys(local).some((k) => !(k in server))) saveSeen({ ...server, ...local });
  }, [user?.id, stored.isSuccess, stored.data, saveSeen]);

  const startTour = useCallback((id: string, howTo?: string) => {
    const g = GUIDES[id];
    if (!g) return;
    setDrawer(null);
    if (!howTo) markSeen(g);
    // A new key restarts the tour from its first step even when the same one is already open.
    setTour({ guide: g, steps: stepsOf(g, howTo), key: Date.now(), howTo });
  }, [markSeen]);

  const openGuide = useCallback((id: string) => { const g = GUIDES[id]; if (g) setDrawer(g); }, []);

  // The first visit to a module starts its tour, once the page has had a moment to draw. It waits for the saved
  // list (or for settings to fail, when this browser's copy decides), so a tour seen on another device stays shut.
  useEffect(() => {
    // Only once the person's record has been read: if it cannot be, no tour opens by itself (the help button still
    // offers it), rather than showing again one this person has already skipped.
    if (!user?.id || tour || autoStarted.current || !stored.isSuccess) return;
    const g = guideFor(pathname);
    if (!g || g.autoStart === false || g.id in seenNow()) return;
    const timer = window.setTimeout(() => { autoStarted.current = true; startTour(g.id); }, 900);
    return () => window.clearTimeout(timer);
  }, [pathname, user?.id, tour, startTour, stored.isSuccess, seenNow]);

  const seen = useCallback((id: string) => id in seenNow(), [seenNow]);
  const seenReady = !!user?.id && stored.isSuccess;
  const value = useMemo(() => ({ startTour, openGuide, tourOpen: !!tour, seen, markSeen: (id: string) => markSeenId(id), seenReady }),
    [startTour, openGuide, tour, seen, markSeenId, seenReady]);
  return (
    <GuideContext.Provider value={value}>
      {children}
      {tour && (
        <GuideTour key={tour.key} guide={tour.guide} steps={tour.steps}
          // Skipped or finished, the main tour stays seen.
          onClose={() => { if (!tour.howTo) markSeen(tour.guide); setTour(null); }}
          onOpenGuide={() => setDrawer(tour.guide)}
          onStartTour={(howTo) => startTour(tour.guide.id, howTo)}
          // A tour that has shown the assistant has introduced it: its own greeting does not follow.
          onStep={(s) => { if (s.target?.split(',').includes('shell-assistant')) markSeenId(ASSISTANT_INTRO); }} />
      )}
      {drawer && <GuideDrawer guide={drawer} opened onClose={() => setDrawer(null)} onStartTour={(howTo) => startTour(drawer.id, howTo)} />}
    </GuideContext.Provider>
  );
}
